import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SelectOption {
  value: unknown;
  label: string;
  disabled?: boolean;
}

/**
 * Custom dropdown that replaces the native <select> so the option list is fully
 * themed (native lists can't be). Implements ControlValueAccessor, so it's a drop-in
 * for `[(ngModel)]`. Built on CDK Menu and reuses the design-system .popover styling.
 */
@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CdkMenuTrigger, CdkMenu, CdkMenuItem],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SelectComponent), multi: true },
  ],
  template: `
    <button
      #trigger
      type="button"
      class="ui-select"
      [disabled]="isDisabled()"
      [attr.aria-label]="ariaLabel()"
      [cdkMenuTriggerFor]="menu"
      (click)="captureWidth()"
      (blur)="onTouched()"
    >
      <span class="ui-select-label" [class.placeholder]="selectedLabel() === ''">
        {{ selectedLabel() || placeholder() }}
      </span>
      <svg class="ui-select-caret" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>

    <ng-template #menu>
      <div class="popover ui-select-menu" cdkMenu [style.min-width.px]="triggerWidth()">
        @for (o of options(); track o.value) {
          <button
            type="button"
            class="popover-item"
            [class.active]="o.value === value()"
            [disabled]="o.disabled"
            cdkMenuItem
            (click)="select(o)"
          >
            <span>{{ o.label }}</span>
            @if (o.value === value()) {
              <svg class="ui-select-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            }
          </button>
        }
      </div>
    </ng-template>
  `,
})
export class SelectComponent implements ControlValueAccessor {
  readonly options = input<SelectOption[]>([]);
  readonly placeholder = input('Select…');
  readonly ariaLabel = input<string>();
  /** Standalone (non-form) disabled binding; form disabling comes via setDisabledState. */
  readonly disabled = input(false);

  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  readonly value = signal<unknown>(null);
  readonly triggerWidth = signal(0);
  private readonly cvaDisabled = signal(false);

  readonly isDisabled = computed(() => this.disabled() || this.cvaDisabled());
  readonly selectedLabel = computed(
    () => this.options().find((o) => o.value === this.value())?.label ?? '',
  );

  onTouched: () => void = () => {};
  private onChange: (v: unknown) => void = () => {};

  captureWidth(): void {
    const el = this.trigger()?.nativeElement;
    if (el) this.triggerWidth.set(el.offsetWidth);
  }

  select(option: SelectOption): void {
    if (option.disabled) return;
    this.value.set(option.value);
    this.onChange(option.value);
    this.onTouched();
  }

  // ---- ControlValueAccessor ----
  writeValue(value: unknown): void {
    this.value.set(value);
  }
  registerOnChange(fn: (v: unknown) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled.set(isDisabled);
  }
}
